/* eslint-disable @typescript-eslint/unbound-method */
import type { Application } from 'pixi.js';
import { Container, UPDATE_PRIORITY } from 'pixi.js';
import { SceneManager } from './SceneManager';
import type { App } from './App';
import type { PopupStackController } from '../popups/PopupStackController';
import { MainMenuScene } from '../mainMenu/MainMenuScene';
import type { GameSceneOptions } from '../game/scene/GameSceneOptions';
import type { DioramaView } from '../game/views/dioramas/DioramaView';
import { GameSceneMVC } from '../game/scene/GameSceneMVC';
import { AppOverlayView } from './appOverlay/AppOverlayView';
import { AppOverlayController } from './appOverlay/AppOverlayController';
import { EditorScene } from '../editor/EditorScene';
import { soundManager } from './SoundManager';
import { VersusGameScene } from '../gameJam/VersusGameScene';

export class AppController {
  public readonly name = 'AppController';

  private pixiApp: Application | null = null;

  private app: App | null = null;

  #sceneManager!: SceneManager;

  #overlay!: AppOverlayController;

  public readonly container: Container = new Container();

  private getPixiApp(): Application {
    if (!this.pixiApp) {
      throw new Error('AppController: Pixi app is not set');
    }

    return this.pixiApp;
  }

  // post
  public init(app: App): void {
    this.app = app;
    this.pixiApp = app.renderer.pixi;

    const { stage, ticker } = this.pixiApp;

    stage.eventMode = 'static';
    stage.interactive = true;

    this.#sceneManager = this.initSceneManager();

    const overlayView = new AppOverlayView();
    this.#overlay = new AppOverlayController(app, overlayView);

    stage.addChild(this.container, overlayView);

    ticker.add(
      () => {
        app.stateController.onTick();
        this.update();
      },
      this,
      UPDATE_PRIORITY.HIGH,
    );

    window.addEventListener('resize', () => {
      this.resize();
    });
    window.addEventListener('deviceorientation', () => {
      this.resize();
    });
    try {
      window.addEventListener('orientationchange', () => {
        this.resize();
      });
    } catch (error) {
      console.error('failed to subscribe to orientationchange:', error);
    }
    window.visualViewport?.addEventListener('resize', () => {
      this.resize();
    });
    this.resize();
  }

  private initSceneManager(): SceneManager {
    const { width, height } = this.getPixiApp().renderer;
    const sceneManager = new SceneManager(width, height);
    this.container.addChild(sceneManager.container);

    sceneManager.onSceneDestroyed.connect(() => {
      const { popups } = this.#overlay;
      popups.view.clearParticles();
    });
    return sceneManager;
  }

  public setFirstScene(): void {
    if (!this.app) {
      throw new Error('AppController: AppController: App is not set');
    }

    const { stateController } = this.app;
    if (stateController.isLevelStarted() || stateController.getLevelNumber() === 1) {
      this.launchGameScene();
    } else {
      this.launchMainMenuScene();
    }

    this.resize();
  }

  public launchEditorScene(): void {
    if (!this.app) return;
    const editorScene = new EditorScene({ app: this.app });
    this.#sceneManager.activateScene(editorScene);
  }

  public launchVersusScene(): void {
    if (!this.app) return;
    const versusScene = new VersusGameScene({ app: this.app });
    this.#sceneManager.activateScene(versusScene);
  }

  public launchGameScene(options?: GameSceneOptions): void {
    if (!this.app) {
      throw new Error('AppController - not initialized!');
    }

    const gameScene = new GameSceneMVC(
      options ?? {
        app: this.app,
        levelData: this.app.stateController.currentLevelData,
        saveProgress: true,
      },
    );
    this.#sceneManager.activateScene(gameScene);
  }

  public launchMainMenuScene(dioramaView?: DioramaView): void {
    if (!this.app) {
      throw new Error('AppController - not initialized!');
    }

    const menuScene = new MainMenuScene(this.app, dioramaView);
    this.#sceneManager.activateScene(menuScene);
  }

  public update(): void {
    const { deltaTime } = this.getPixiApp().ticker;

    this.#sceneManager.update(deltaTime);
    this.#overlay.update(deltaTime);
    soundManager.update(deltaTime);
  }

  public resize(): void {
    const { width, height } = this.getPixiApp().renderer;
    this.#sceneManager.resize(width, height);
  }

  public get sceneManager(): SceneManager {
    return this.#sceneManager;
  }

  public get overlay(): AppOverlayController {
    return this.#overlay;
  }

  public get popups(): PopupStackController {
    return this.#overlay.popups;
  }
}
